/**
 * EmbedBridge singleton — Phase 4-6 / T30.
 *
 * The bootstrap in src/lib/embed/bootstrap.ts creates a single EmbedBridge per
 * widget mount, but Phase 4-6 introduces components (CustomerContextPanel,
 * LinkedEntitiesList, ConversationHeader) that need to post `openHostEntity`
 * etc. without having the bridge passed through every prop chain.
 *
 * We expose a module-level singleton store: bootstrap calls `setBridge(b)` once
 * SSO completes, and components import { bridge } from this module to call
 * `bridge.send(...)`. Plain non-reactive ref — components that need reactive
 * "is the bridge ready?" semantics can read `bridgeReady.current` ($state rune).
 *
 * Importers:
 *   - src/lib/embed/bootstrap.ts — calls setBridge(bridge) after bridge.start().
 *   - src/components/CustomerContextPanel.svelte
 *   - src/components/LinkedEntitiesList.svelte
 *   - src/components/ConversationHeader.svelte
 */

import type { EmbedBridge } from '@platform/embed-bridge';
import { newEnvelopeId } from '@platform/embed-bridge';

let _bridge: EmbedBridge | null = null;

export const bridgeReady = $state({ current: false });

/** Bootstrap calls this once the EmbedBridge is created. */
export function setBridge(bridge: EmbedBridge): void {
  _bridge = bridge;
  bridgeReady.current = true;
}

/** Returns the singleton bridge (null until bootstrap registers it). */
export function getBridge(): EmbedBridge | null {
  return _bridge;
}

/** Convenience — post openHostEntity if bridge is ready. */
export function postOpenHostEntity(entityType: string, entityId: string): void {
  const b = _bridge;
  if (!b) return;
  b.send({
    v: 1,
    type: 'openHostEntity',
    id: newEnvelopeId(),
    data: { entityType, entityId },
  });
}

/** Convenience — post createHostEntityRequest if bridge is ready. */
export function postCreateEntityRequest(
  entityType: string,
  conversationId: string,
  prefill?: Record<string, unknown>,
): void {
  const b = _bridge;
  if (!b) return;
  b.send({
    v: 1,
    type: 'createHostEntityRequest',
    id: newEnvelopeId(),
    data: { entityType, conversationId, prefill },
  });
}
